import type { I18n, MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import type { TRPCClientErrorLike } from "@trpc/client";
import { useEffect } from "react";
import type { FieldValues, Path, UseFormSetError } from "react-hook-form";
import type * as z from "zod/v4";

export type ServerValidationException = z.ZodFlattenedError<
	any,
	{
		message: string;
		errorCode: keyof typeof z.ZodIssueCode;
		params?:
			| {
					i18n?: MessageDescriptor;
					i18nValues?: Record<string, unknown>;
					[k: string]: any;
			  }
			| undefined;
	}
>;

export type ServerError = TRPCClientErrorLike<any> | null | undefined;

const genericServerErrorMessage = msg`Something went wrong`;

type ServerIssue = {
	message?: unknown;
	params?: {
		i18n?: Partial<Pick<MessageDescriptor, "id" | "message">>;
		i18nValues?: unknown;
	};
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null && !Array.isArray(value);

const isSnakeCaseWireKey = (value: string) => /^[a-z][a-z0-9]*(?:_[a-z0-9]+)+$/.test(value);

const displayMessage = (value: unknown): string | undefined =>
	typeof value === "string" && value.trim().length > 0 && !isSnakeCaseWireKey(value) ? value : undefined;

const renderServerIssueMessage = ({ i18n, issue }: { i18n: I18n; issue: ServerIssue }) => {
	const descriptor = issue.params?.i18n;
	const fallbackMessage = descriptor
		? (displayMessage(descriptor.message) ?? i18n._(genericServerErrorMessage))
		: (displayMessage(issue.message) ?? i18n._(genericServerErrorMessage));

	if (!descriptor || typeof descriptor.id !== "string" || descriptor.id.trim().length === 0) {
		return fallbackMessage;
	}

	try {
		const renderedMessage = i18n._(
			descriptor.id,
			isRecord(issue.params?.i18nValues) ? issue.params.i18nValues : undefined,
			{ message: fallbackMessage },
		);
		return displayMessage(renderedMessage) ?? fallbackMessage;
	} catch {
		return fallbackMessage;
	}
};

// This converts our server errors to a format that react-hook-form understands
export function addServerError<T>(
	error: { attribute: string; details: string[] },
	setError: (name: Path<T>, error: { message: string; type: string }) => void,
) {
	setError(error.attribute as Path<T>, {
		// Rule Name
		message: error.details.join(". "),
		type: "server",
	});
}

export const useServerErrorToFormError = <T extends FieldValues>({
	serverError,
	setError,
}: {
	serverError?: ServerError | undefined | null;
	setError: UseFormSetError<T>;
}) => {
	const { i18n } = useLingui();
	useEffect(() => {
		if (serverError?.data?.errors?.fieldErrors) {
			const { fieldErrors } = serverError.data.errors as ServerValidationException;
			for (const key in fieldErrors) {
				const message = fieldErrors[key]?.map((e: ServerIssue) => renderServerIssueMessage({ i18n, issue: e }));

				if (message) {
					addServerError({ attribute: key, details: message }, setError);
				}
			}
		}
	}, [i18n, serverError, setError]);
};
